import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Send, Loader2, Image, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface Comment {
  id: string;
  body: string;
  created_at: string;
  author_name: string | null;
  author_email: string | null;
  is_external: boolean;
  user_id: string | null;
  image_url: string | null;
}

interface CommentsSectionProps {
  documentId: string;
  shareLinkId: string;
  permission: string;
  isOwner?: boolean;
}

const CommentsSection = ({ documentId, shareLinkId, permission, isOwner = false }: CommentsSectionProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [authorEmail, setAuthorEmail] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const canComment = permission === "read_comment" || isOwner;

  useEffect(() => {
    fetchComments();
    
    // Subscribe to realtime updates
    const channel = supabase
      .channel(`comments-${documentId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'comments',
          filter: `document_id=eq.${documentId}`
        },
        (payload) => {
          const newComment = payload.new as Comment;
          setComments(prev => [...prev, newComment]);
          // Auto scroll to bottom
          setTimeout(() => {
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
          }, 100);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [documentId]);

  const fetchComments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("comments")
      .select("*")
      .eq("document_id", documentId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching comments:", error);
    } else {
      setComments(data || []);
    }
    setLoading(false);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return null;

    setUploadingImage(true);
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${imageFile.name.split('.').pop()}`;
    const filePath = `${shareLinkId}/${fileName}`;

    const { error } = await supabase.storage
      .from('comment-attachments')
      .upload(filePath, imageFile);

    setUploadingImage(false);

    if (error) {
      console.error("Upload error:", error);
      toast.error("Erreur lors de l'upload de l'image");
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('comment-attachments')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSubmit = async () => {
    if (!newComment.trim() && !imageFile) {
      toast.error("Ajoutez un message ou une image");
      return;
    }
    
    if (!authorName.trim() && !isOwner) {
      toast.error("Veuillez indiquer votre nom");
      return;
    }

    setSubmitting(true);

    let imageUrl: string | null = null;
    if (imageFile) {
      imageUrl = await uploadImage();
    }

    const commentBody = newComment.trim();
    const commentAuthorName = isOwner ? "Propriétaire du document" : authorName.trim();
    
    const { error } = await supabase.from("comments").insert({
      document_id: documentId,
      share_link_id: shareLinkId,
      body: commentBody,
      author_name: commentAuthorName,
      author_email: authorEmail.trim() || null,
      is_external: !isOwner,
      image_url: imageUrl,
    });

    if (error) {
      console.error("Error submitting comment:", error);
      toast.error("Erreur lors de l'envoi");
    } else {
      setNewComment("");
      removeImage();
      
      // Notify document owner (don't wait for response)
      if (!isOwner) {
        supabase.functions.invoke("notify-comment", {
          body: {
            documentId,
            shareLinkId,
            commentAuthor: commentAuthorName,
            commentPreview: commentBody,
          },
        }).catch(console.error);
      }
    }
    setSubmitting(false);
  };

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTime = (dateStr: string) => {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: fr });
  };

  return (
    <div className="border-t border-border/50 bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/20">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Discussion</h2>
            <p className="text-xs text-muted-foreground">
              {comments.length} message{comments.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Messages List - Chat Style */}
            <div 
              ref={scrollRef}
              className="max-h-[400px] overflow-y-auto mb-5 space-y-4 scroll-smooth"
            >
              {comments.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-7 h-7 text-muted-foreground/50" />
                  </div>
                  <p className="text-muted-foreground text-sm font-medium">
                    Démarrez la conversation
                  </p>
                  <p className="text-muted-foreground/70 text-xs mt-1">
                    Posez une question ou laissez un commentaire
                  </p>
                </div>
              ) : (
                comments.map((comment) => {
                  const isOwnerMessage = !comment.is_external;
                  return (
                    <div 
                      key={comment.id} 
                      className={cn(
                        "flex gap-3",
                        isOwnerMessage && "flex-row-reverse"
                      )}
                    >
                      <Avatar className={cn(
                        "w-9 h-9 shrink-0 ring-2 ring-offset-2 ring-offset-background",
                        isOwnerMessage ? "ring-primary" : "ring-muted"
                      )}>
                        <AvatarFallback className={cn(
                          "text-xs font-medium",
                          isOwnerMessage 
                            ? "bg-primary text-primary-foreground" 
                            : "bg-muted text-muted-foreground"
                        )}>
                          {getInitials(comment.author_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className={cn(
                        "flex-1 max-w-[80%]",
                        isOwnerMessage && "flex flex-col items-end"
                      )}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn(
                            "font-medium text-sm",
                            isOwnerMessage && "text-primary"
                          )}>
                            {comment.author_name || "Anonyme"}
                          </span>
                          {isOwnerMessage && (
                            <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">
                              Propriétaire
                            </span>
                          )}
                          <span className="text-[11px] text-muted-foreground">
                            {formatTime(comment.created_at)}
                          </span>
                        </div>
                        <div className={cn(
                          "rounded-2xl px-4 py-2.5 shadow-sm",
                          isOwnerMessage 
                            ? "bg-primary text-primary-foreground rounded-tr-sm" 
                            : "bg-white dark:bg-muted border border-border/50 rounded-tl-sm"
                        )}>
                          {comment.body && (
                            <p className="text-sm whitespace-pre-wrap leading-relaxed">
                              {comment.body}
                            </p>
                          )}
                          {comment.image_url && (
                            <img 
                              src={comment.image_url} 
                              alt="Pièce jointe"
                              className={cn(
                                "mt-2 rounded-lg max-w-full max-h-[200px] object-cover cursor-pointer hover:opacity-90 transition-opacity",
                                !comment.body && "mt-0"
                              )}
                              onClick={() => window.open(comment.image_url!, '_blank')}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Input Area */}
            {canComment ? (
              <div className="space-y-3">
                {/* Author info for external users */}
                {!isOwner && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Votre nom *"
                      value={authorName}
                      onChange={(e) => setAuthorName(e.target.value)}
                      className="text-sm h-10 bg-white dark:bg-muted border-border/50"
                    />
                    <Input
                      type="email"
                      placeholder="Email (optionnel)"
                      value={authorEmail}
                      onChange={(e) => setAuthorEmail(e.target.value)}
                      className="text-sm h-10 bg-white dark:bg-muted border-border/50"
                    />
                  </div>
                )}

                {/* Image Preview */}
                {imagePreview && (
                  <div className="relative inline-block">
                    <img 
                      src={imagePreview} 
                      alt="Aperçu" 
                      className="h-20 rounded-lg border border-border/50 object-cover"
                    />
                    <button
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center shadow-md hover:bg-destructive/90 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* Message Input */}
                <div className="flex gap-2 items-end">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={submitting}
                  >
                    <Image className="w-5 h-5 text-muted-foreground" />
                  </Button>
                  <Textarea
                    placeholder="Votre message..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit();
                      }
                    }}
                    rows={1}
                    className="resize-none text-sm min-h-[40px] max-h-[120px] bg-white dark:bg-muted border-border/50"
                  />
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || uploadingImage || (!newComment.trim() && !imageFile)}
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl shadow-md"
                  >
                    {submitting || uploadingImage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground text-center">
                  Appuyez sur Entrée pour envoyer • Shift+Entrée pour un saut de ligne
                </p>
              </div>
            ) : (
              <div className="text-center py-4 px-6 rounded-xl bg-muted/50 border border-border/50">
                <p className="text-sm text-muted-foreground">
                  Les commentaires sont désactivés pour ce document
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CommentsSection;